'use client';

import { Fragment } from 'react';
import type { PythonCodeBlock } from '@jeraisonne/ai/visual-blocks';

// Coloration syntaxique maison (aucune librairie externe). Tokenisation
// ligne-par-ligne : suffisant pour les extraits d'exercices.

const PY_KEYWORDS = new Set([
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'False',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'None',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'True',
  'try',
  'while',
  'with',
  'yield',
  'match',
  'case',
]);

const PY_BUILTINS = new Set([
  'abs',
  'all',
  'any',
  'bool',
  'dict',
  'enumerate',
  'float',
  'input',
  'int',
  'len',
  'list',
  'map',
  'max',
  'min',
  'open',
  'print',
  'range',
  'round',
  'set',
  'sorted',
  'str',
  'sum',
  'tuple',
  'type',
  'zip',
]);

const TOKEN_RE =
  /(#[^\n]*)|("(?:\\.|[^"\\])*"?|'(?:\\.|[^'\\])*'?)|(\d[\d_]*\.?\d*)|([A-Za-z_]\w*)/g;

type Piece = { text: string; cls: string };

function tokenizeLine(line: string): Piece[] {
  const pieces: Piece[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(line)) !== null) {
    if (m.index > last) pieces.push({ text: line.slice(last, m.index), cls: '' });
    const [, comment, str, num, ident] = m;
    if (comment !== undefined) {
      pieces.push({ text: comment, cls: 'text-slate-400 italic' });
    } else if (str !== undefined) {
      pieces.push({ text: str, cls: 'text-emerald-600' });
    } else if (num !== undefined) {
      pieces.push({ text: num, cls: 'text-amber-600' });
    } else if (ident !== undefined) {
      const cls = PY_KEYWORDS.has(ident)
        ? 'text-violet-600 font-semibold'
        : PY_BUILTINS.has(ident)
          ? 'text-sky-600'
          : 'text-slate-800';
      pieces.push({ text: ident, cls });
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) pieces.push({ text: line.slice(last), cls: '' });
  return pieces;
}

export function PythonCodeBlockRenderer({ block }: { block: PythonCodeBlock }) {
  const lines = block.code.replace(/\n$/, '').split('\n');
  const showLineNumbers = block.showLineNumbers !== false;
  const highlight = new Set(block.highlightLines ?? []);

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50">
      <pre className="m-0 p-0 text-xs leading-relaxed sm:text-[13px]">
        <code className="block font-mono">
          {lines.map((line, i) => {
            const lineNo = i + 1;
            const isHl = highlight.has(lineNo);
            return (
              <div key={i} className={`flex ${isHl ? 'bg-amber-100/70' : ''}`}>
                {showLineNumbers && (
                  <span
                    aria-hidden
                    className="select-none border-r border-slate-200 px-2 py-0.5 text-right text-slate-300"
                    style={{ minWidth: '2.5rem' }}
                  >
                    {lineNo}
                  </span>
                )}
                <span className="whitespace-pre px-3 py-0.5">
                  {tokenizeLine(line).map((p, j) => (
                    <Fragment key={j}>
                      {p.cls ? <span className={p.cls}>{p.text}</span> : p.text}
                    </Fragment>
                  ))}
                  {line.length === 0 ? ' ' : null}
                </span>
              </div>
            );
          })}
        </code>
      </pre>
    </div>
  );
}
